import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Firestore, doc, docData, updateDoc } from '@angular/fire/firestore';
import { Merchant } from '@print2go/types';
import {
  loadMerchantSuccess,
  loadMerchantFailure,
  updateMerchant,
  updateMerchantSuccess,
  updateMerchantFailure,
} from '../actions/merchant.actions';
import { setAuthState } from '../actions/user.actions';
import { MessageService } from 'primeng/api';

@Injectable()
export class MerchantEffects {
  private firestore = inject(Firestore);
  private actions$ = inject(Actions);
  private messageService = inject(MessageService);

  loadMerchant$ = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(setAuthState),
        switchMap(({ user: firebaseUser }) => {
          if (!firebaseUser) return of(loadMerchantSuccess({ merchant: null }));

          const merchantRef = doc(this.firestore, 'merchants', firebaseUser.uid);
          return docData(merchantRef, { idField: 'uid' })
            .pipe(
              map(merchant => {
                if (!merchant) {
                  return loadMerchantFailure({ error: new Date(`Merchant ${firebaseUser.uid} was not found`) });
                }

                return loadMerchantSuccess({ merchant: merchant as Merchant });
              }),
              catchError(error => of(loadMerchantFailure(error))),
            )
        })
      )
  });
  
  updateMerchant$ = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(updateMerchant),
        switchMap(({ data }) => {
          const { uid, ...merchantUpdates } = data;
          const merchantRef = doc(this.firestore, 'merchants', uid);
          return from(updateDoc(merchantRef, merchantUpdates))
            .pipe(
              map(() => {
                return updateMerchantSuccess({ data: { ...merchantUpdates, uid } as Merchant });
              }),
              catchError(error => of(updateMerchantFailure({ error })))
            );
        }),
      );
  });

  updateSuccessToast$ = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(updateMerchantSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Merchant updated successfully',
          });
        }),
      );
  }, { dispatch: false });

  updateFailureToast$ = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(updateMerchantFailure),
        tap(({ error }) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to update merchant: ${error.message || error}`,
          });
        }),
      );
  }, { dispatch: false });
}